"use client";

import { useState } from "react";
import Link from "next/link";
import { ArrowRight } from "lucide-react";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { Button } from "@/components/ui/button";
import { FAQ_ITEMS, type FaqItem } from "@/lib/data/faq";
import { cn } from "@/lib/utils";
import { SubscriptionPlansDialog } from "./subscription-plans-dialog";

type FaqSectionProps = {
  showContactCta?: boolean;
  contactHref?: string;
  id?: string;
  className?: string;
};

/**
 * FAQ cevabındaki token'lara göre renderer:
 * - `tokens.subscription`: metni abonelik pop-up'ını açan butona çevirir.
 * - `tokens.link`: metni verilen href'e giden Next Link'e çevirir.
 * Token yoksa düz metin basılır.
 */
function FaqAnswer({
  item,
  onOpenSubscription,
}: {
  item: FaqItem;
  onOpenSubscription: () => void;
}) {
  const subscriptionToken = item.tokens?.subscription;
  if (subscriptionToken) {
    const idx = item.answer.indexOf(subscriptionToken);
    if (idx !== -1) {
      const before = item.answer.slice(0, idx);
      const after = item.answer.slice(idx + subscriptionToken.length);
      return (
        <>
          {before}
          <button
            type="button"
            onClick={onOpenSubscription}
            className="text-primary font-semibold underline-offset-2 hover:underline"
          >
            {subscriptionToken}
          </button>
          {after}
        </>
      );
    }
  }

  const linkToken = item.tokens?.link;
  if (linkToken) {
    const idx = item.answer.indexOf(linkToken.text);
    if (idx !== -1) {
      const before = item.answer.slice(0, idx);
      const after = item.answer.slice(idx + linkToken.text.length);
      return (
        <>
          {before}
          <Link
            href={linkToken.href}
            className="text-primary font-semibold underline-offset-2 hover:underline"
          >
            {linkToken.text}
          </Link>
          {after}
        </>
      );
    }
  }

  return <>{item.answer}</>;
}

export function FaqSection({
  showContactCta = false,
  contactHref = "/about#iletisim",
  id,
  className,
}: FaqSectionProps) {
  const [isSubscriptionOpen, setIsSubscriptionOpen] = useState(false);

  const leftColumn = FAQ_ITEMS.slice(0, 4);
  const rightColumn = FAQ_ITEMS.slice(4);

  return (
    <section id={id} className={cn("scroll-mt-20 px-4 py-10 md:px-6 md:py-16 lg:px-8", className)}>
      <div className="mx-auto max-w-6xl">
        <div className="text-center">
          <h2 className="text-foreground text-2xl font-bold tracking-tight md:text-3xl">
            Sıkça Sorulan Sorular
          </h2>
          <p className="text-muted-foreground mx-auto mt-3 max-w-xl text-sm md:text-base">
            Sorular aklınızda konaklamasın.
          </p>
        </div>

        <div className="mt-10 grid grid-cols-1 gap-4 md:grid-cols-2">
          <Accordion type="single" collapsible className="w-full space-y-4">
            {leftColumn.map((faq, index) => (
              <AccordionItem
                key={index}
                value={`left-${index}`}
                // `last:border-b`: ui/accordion'daki varsayılan `last:border-b-0` (çizgili
                // liste için) kart tasarımında her sütunun SON kartının alt çerçevesini
                // siliyordu → son satır "arkada kalmış" görünüyordu (2026-10-24).
                className="border-border/50 data-[state=open]:border-primary/30 rounded-2xl border bg-white px-6 last:border-b data-[state=open]:shadow-md"
              >
                <AccordionTrigger className="py-5 text-left font-semibold hover:no-underline">
                  {faq.question}
                </AccordionTrigger>
                <AccordionContent className="text-muted-foreground pb-5 leading-relaxed">
                  <FaqAnswer item={faq} onOpenSubscription={() => setIsSubscriptionOpen(true)} />
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
          <Accordion type="single" collapsible className="w-full space-y-4">
            {rightColumn.map((faq, index) => (
              <AccordionItem
                key={index}
                value={`right-${index}`}
                className="border-border/50 data-[state=open]:border-primary/30 rounded-2xl border bg-white px-6 last:border-b data-[state=open]:shadow-md"
              >
                <AccordionTrigger className="py-5 text-left font-semibold hover:no-underline">
                  {faq.question}
                </AccordionTrigger>
                <AccordionContent className="text-muted-foreground pb-5 leading-relaxed">
                  <FaqAnswer item={faq} onOpenSubscription={() => setIsSubscriptionOpen(true)} />
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>

        {showContactCta && (
          <div className="mt-12 text-center">
            <p className="text-muted-foreground">Başka sorularınız mı var?</p>
            <Link href={contactHref} className="mt-4 inline-block">
              <Button variant="outline" className="gap-2 rounded-full">
                Bize Ulaşın
                <ArrowRight className="h-4 w-4" />
              </Button>
            </Link>
          </div>
        )}
      </div>

      <SubscriptionPlansDialog open={isSubscriptionOpen} onOpenChange={setIsSubscriptionOpen} />
    </section>
  );
}
